import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, HeartOff, PackageX } from "lucide-react";
import { toast } from "sonner";
import { chf } from "../lib/format";
import { useFavoritesStore } from "../store/favoritesStore";
import { useCartStore } from "../store/cartStore";
import { fetchFavorites } from "../lib/api";

export default function Favoris() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const toggle = useFavoritesStore((s) => s.toggle);
  const load = useFavoritesStore((s) => s.load);
  const addItem = useCartStore((s) => s.addItem);

  const refresh = async () => {
    setLoading(true);
    try {
      const data = await fetchFavorites();
      setItems(data.favorites || []);
      await load();
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (p) => {
    try {
      await toggle(p);
      setItems((prev) => prev.filter((x) => x.id !== p.id));
      toast.success("Retiré des favoris");
    } catch {
      toast.error("Action impossible");
    }
  };

  const add = (p) => {
    addItem({ id: p.id, title: p.title, price: p.price, image_url: p.image_url });
    toast.success(`${(p.title || "").slice(0, 40)} ajouté au panier`);
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-extrabold mb-1" style={{ color: "#0b1220" }}>Mes favoris</h1>
      <p className="text-sm mb-6" style={{ color: "#64748b" }}>
        Vos produits préférés — prix et disponibilités mis à jour depuis le catalogue.
      </p>

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => <div key={i} className="h-64 rounded-2xl animate-pulse" style={{ background: "#e9edf2" }} />)}
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 gap-3" style={{ color: "#94a3b8" }}>
          <HeartOff size={48} />
          <p className="font-semibold">Aucun favori pour le moment.</p>
          <button onClick={() => nav("/boutique")} data-testid="fav-empty-shop"
            className="px-5 py-2 rounded-full text-sm font-bold text-white" style={{ background: "#075a43" }}>
            Découvrir la boutique
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="favorites-grid">
          {items.map((p) => (
            <div key={p.id} data-testid={`favorite-card-${p.id}`}
              className="rounded-2xl overflow-hidden bg-white flex flex-col" style={{ border: "1px solid #f0f0f0" }}>
              <div className="relative aspect-square cursor-pointer" style={{ background: "#f8fafc" }}
                onClick={() => !p.unavailable && nav(`/produit/${encodeURIComponent(p.id)}`, { state: { product: p } })}>
                {p.image_url && <img src={p.image_url} alt={p.title} className="w-full h-full object-contain p-2" />}
                {p.unavailable && (
                  <div className="absolute inset-0 flex items-center justify-center" style={{ background: "rgba(255,255,255,0.7)", color: "#94a3b8" }}>
                    <PackageX size={32} />
                  </div>
                )}
                <button onClick={(e) => { e.stopPropagation(); remove(p); }} data-testid={`fav-remove-${p.id}`}
                  className="absolute top-2 right-2 w-9 h-9 rounded-full flex items-center justify-center"
                  style={{ background: "#fff", boxShadow: "0 2px 6px rgba(0,0,0,.15)" }}>
                  <Heart size={18} color="#e11d48" fill="#e11d48" />
                </button>
              </div>
              <div className="p-3 flex-1 flex flex-col">
                <p className="text-sm font-semibold line-clamp-2 flex-1" style={{ color: "#0b1220" }}>{p.title}</p>
                <div className="flex items-center justify-between mt-3">
                  <span className="font-extrabold" style={{ color: "#0b1220" }}>
                    {p.unavailable ? "Indisponible" : chf(p.price)}
                  </span>
                  {!p.unavailable && (
                    <button onClick={() => add(p)} data-testid={`fav-add-cart-${p.id}`}
                      className="px-3 py-2 rounded-xl text-white text-sm font-bold" style={{ background: "#f97316" }}>
                      Panier
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
